import type { JudgeConfig } from '@meridian-ssa/shared';
import { useState } from 'react';

import { apiRequest } from '@/config';

const DEFAULT_SYSTEM_PROMPT = `You are an expert spreadsheet evaluator. Your task is to compare two spreadsheet outputs and determine which one better fulfills the given task requirements.

You will be given:
1. The original task/prompt
2. Two spreadsheet JSON outputs (Model A and Model B)

Evaluate based on:
- **Correctness**: Does the output correctly solve the stated problem?
- **Completeness**: Does it include all required elements?
- **Formula accuracy**: Are formulas correct and properly referenced?
- **Data quality**: Are values accurate and formatted appropriately?
- **Structure**: Is the sheet well-organized and easy to understand?

Respond with a JSON object containing:
- "winner": "a" | "b" | "tie" | "both_bad"
- "reasoning": A brief explanation of your decision (2-3 sentences)

Choose:
- "a" if Model A is clearly better
- "b" if Model B is clearly better
- "tie" if both are roughly equal in quality
- "both_bad" if both fail to adequately address the task`;

const DEFAULT_USER_PROMPT = `# Task/Prompt
{{ORIGINAL_PROMPT}}

# Model A Output
{{RESULT_A}}

# Model B Output
{{RESULT_B}}

Based on the task requirements, which model produced the better spreadsheet? Respond with JSON only.`;

interface JudgeConfigFormProps {
  onSuccess: (newConfig: JudgeConfig) => void;
  onCancel?: () => void;
}

export function JudgeConfigForm({ onSuccess, onCancel }: JudgeConfigFormProps) {
  const [formData, setFormData] = useState({
    name: '',
    provider: 'openai' as 'openai' | 'anthropic' | 'xai' | 'gemini',
    api_model: 'gpt-4o',
    system_prompt: DEFAULT_SYSTEM_PROMPT,
    user_prompt_template: DEFAULT_USER_PROMPT,
    temperature: 0.3,
    max_tokens: 500,
    created_by: '',
  });
  const [errors, setErrors] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);

  function validateForm(): string[] {
    const errors: string[] = [];

    if (!formData.name.trim()) {
      errors.push('Name is required');
    }

    if (!formData.api_model.trim()) {
      errors.push('API model is required');
    }

    if (!formData.system_prompt.trim()) {
      errors.push('System prompt is required');
    }

    if (!formData.user_prompt_template.trim()) {
      errors.push('User prompt template is required');
    }

    // Check for required placeholders
    const requiredPlaceholders = [
      '{{ORIGINAL_PROMPT}}',
      '{{RESULT_A}}',
      '{{RESULT_B}}',
    ];
    const combinedPrompt =
      formData.system_prompt + '\n' + formData.user_prompt_template;

    const missingPlaceholders = requiredPlaceholders.filter(
      placeholder => !combinedPrompt.includes(placeholder)
    );

    if (missingPlaceholders.length > 0) {
      errors.push(
        `Missing required placeholders: ${missingPlaceholders.join(', ')}`
      );
    }

    return errors;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    const validationErrors = validateForm();
    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      setSubmitting(true);
      setErrors([]);

      const newConfig = await apiRequest('/api/judge-configs', {
        method: 'POST',
        body: JSON.stringify(formData),
      });

      onSuccess(newConfig);
    } catch (err: any) {
      setErrors([err.message || 'Failed to create judge config']);
    } finally {
      setSubmitting(false);
    }
  }

  function handleProviderChange(
    provider: 'openai' | 'anthropic' | 'xai' | 'gemini'
  ) {
    // Suggest default models for each provider
    const defaultModels = {
      openai: 'gpt-4o',
      anthropic: 'claude-sonnet-4-20250514',
      xai: 'grok-2-latest',
      gemini: 'gemini-2.0-flash-exp',
    };

    setFormData({
      ...formData,
      provider,
      api_model: defaultModels[provider],
    });
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {errors.length > 0 && (
        <div className="p-4 bg-red-50 border border-red-200 rounded">
          <h3 className="font-semibold text-red-900 mb-2">
            Validation Errors:
          </h3>
          <ul className="list-disc list-inside text-red-800 space-y-1">
            {errors.map((error, i) => (
              <li key={i}>{error}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Basic Info */}
      <div className="bg-white shadow-md rounded-lg p-6 border border-[#D1C9C4]">
        <h2 className="text-xl font-semibold mb-4 text-text">
          Basic Information
        </h2>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-text-light mb-1">
              Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={e => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border border-[#D1C9C4] rounded focus:outline-none focus:ring-2 focus:ring-accent"
              placeholder="e.g., GPT-4o Strict Judge"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-text-light mb-1">
              Provider *
            </label>
            <select
              value={formData.provider}
              onChange={e => handleProviderChange(e.target.value as any)}
              className="w-full px-3 py-2 border border-[#D1C9C4] rounded focus:outline-none focus:ring-2 focus:ring-accent"
            >
              <option value="openai">OpenAI</option>
              <option value="anthropic">Anthropic</option>
              <option value="xai">xAI (Grok)</option>
              <option value="gemini">Google (Gemini)</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-text-light mb-1">
              API Model *
            </label>
            <input
              type="text"
              value={formData.api_model}
              onChange={e =>
                setFormData({ ...formData, api_model: e.target.value })
              }
              className="w-full px-3 py-2 border border-[#D1C9C4] rounded focus:outline-none focus:ring-2 focus:ring-accent"
              placeholder="e.g., gpt-4o, claude-sonnet-4-20250514"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-text-light mb-1">
              Created By (optional)
            </label>
            <input
              type="text"
              value={formData.created_by}
              onChange={e =>
                setFormData({ ...formData, created_by: e.target.value })
              }
              className="w-full px-3 py-2 border border-[#D1C9C4] rounded focus:outline-none focus:ring-2 focus:ring-accent"
              placeholder="Your name or team"
            />
          </div>
        </div>
      </div>

      {/* Prompts */}
      <div className="bg-white shadow-md rounded-lg p-6 border border-[#D1C9C4]">
        <h2 className="text-xl font-semibold mb-2 text-text">Prompts</h2>
        <p className="text-sm text-text-light mb-4">
          Required placeholders:{' '}
          <code className="bg-[#F5F1EE] px-1 rounded">
            {'{{'} ORIGINAL_PROMPT{'}}'}
          </code>
          ,{' '}
          <code className="bg-[#F5F1EE] px-1 rounded">
            {'{{'} RESULT_A{'}}'}
          </code>
          ,{' '}
          <code className="bg-[#F5F1EE] px-1 rounded">
            {'{{'} RESULT_B{'}}'}
          </code>
        </p>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-text-light mb-1">
              System Prompt *
            </label>
            <textarea
              value={formData.system_prompt}
              onChange={e =>
                setFormData({ ...formData, system_prompt: e.target.value })
              }
              rows={12}
              className="w-full px-3 py-2 border border-[#D1C9C4] rounded focus:outline-none focus:ring-2 focus:ring-accent font-mono text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-text-light mb-1">
              User Prompt Template *
            </label>
            <textarea
              value={formData.user_prompt_template}
              onChange={e =>
                setFormData({
                  ...formData,
                  user_prompt_template: e.target.value,
                })
              }
              rows={8}
              className="w-full px-3 py-2 border border-[#D1C9C4] rounded focus:outline-none focus:ring-2 focus:ring-accent font-mono text-sm"
            />
          </div>
        </div>
      </div>

      {/* Model Parameters */}
      <div className="bg-white shadow-md rounded-lg p-6 border border-[#D1C9C4]">
        <h2 className="text-xl font-semibold mb-4 text-text">
          Model Parameters
        </h2>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-text-light mb-1">
              Temperature: {formData.temperature}
            </label>
            <input
              type="range"
              min="0"
              max="1"
              step="0.1"
              value={formData.temperature}
              onChange={e =>
                setFormData({
                  ...formData,
                  temperature: parseFloat(e.target.value),
                })
              }
              className="w-full"
            />
            <p className="text-xs text-text-light mt-1">
              Lower = more consistent, Higher = more creative
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-text-light mb-1">
              Max Tokens
            </label>
            <input
              type="number"
              min="100"
              max="4000"
              value={formData.max_tokens}
              onChange={e =>
                setFormData({
                  ...formData,
                  max_tokens: parseInt(e.target.value),
                })
              }
              className="w-full px-3 py-2 border border-[#D1C9C4] rounded focus:outline-none focus:ring-2 focus:ring-accent"
            />
            <p className="text-xs text-text-light mt-1">
              Maximum tokens for the response (reasoning should be concise)
            </p>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-between">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="px-6 py-2 border border-[#D1C9C4] rounded hover:bg-[#F5F1EE] text-text"
          >
            Cancel
          </button>
        )}
        <button
          type="submit"
          disabled={submitting}
          className="px-6 py-2 bg-accent text-white rounded hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed ml-auto"
        >
          {submitting ? 'Creating...' : 'Create Judge Config'}
        </button>
      </div>

      <div className="p-4 bg-yellow-50 border border-yellow-200 rounded">
        <h3 className="font-semibold text-yellow-900 mb-2">Important</h3>
        <ul className="text-sm text-yellow-800 space-y-1">
          <li>
            • Judge configs are immutable once created (cannot be edited later)
          </li>
          <li>
            • Make sure your prompts and settings are correct before creating
          </li>
          <li>• Test your judge thoroughly before using in production</li>
        </ul>
      </div>
    </form>
  );
}
